import {
  DetailsListLayoutMode,
  IColumn,
  mergeStyles,
  MessageBar,
  MessageBarType,
  ScrollablePane,
  ScrollbarVisibility,
  SelectionMode,
  ShimmeredDetailsList,
  Stack,
  Sticky,
  StickyPositionType,
  Text,
} from "@fluentui/react";
import { ExperimentType, PullRequestChange } from "../../Types";
import { getArchiveTableColumns } from "./archiveTableColumnDefinitions";
import { styles } from "../experimentsTable.styles";
import { styles as sharedStyles } from "../../Shared/shared.styles";
import FilterComponent from "../../Shared/FilterComponent/FilterComponent";
import { useMemo, useState } from "react";
import { useTableFiltering } from "../../Hooks/useTableFiltering";
import { useTableSorting } from "../../Hooks/useTableSorting";
import { createColumnClickHandler } from "../../Shared/shared.utils";
import { getFilters } from "./archiveTableFilters";
import { useBoolean } from "@fluentui/react-hooks";
import SharedDialog from "../../Experiment/RolloutDetails/SharedDialog";
import {
  createAdoBranch,
  createAdoPullRequest,
  fetchAdoContent,
  pushToAdoBranch,
} from "../../Api/ado.api";
import { ARCHIVE_KEY, UNARCHIVE_KEY } from "../../Shared/shared.consts";
import useAppInsights from "../../Hooks/UseAppInsights";
import {
  baseTrackExceptionEvent,
  createBaseOperationTracker,
} from "../../Shared/AppInsights";
import debounce from "lodash/debounce";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import FilterTotalsComponent from "../../Shared/FilterComponent/FilterTotalsComponent";
import useLocalStorageState from "../../Hooks/useLocalStorageStage";
import { useEv2ExperimentParams } from "../../Hooks/UseCurrentExtension";
import { useDataAccessContext } from "../../Contexts/DataAccessContext";

interface ArchiveTableProps {
  /**
   * The list of archived experiments to display in the table.
   */
  items: ExperimentType[];
  /**
   * Callback function to refresh the experiments list after successful unarchiving.
   */
  refreshExperiments: () => void;
}

/**
 * Table component to display archived experiments with filtering, sorting, and unarchive functionality.
 */
const ArchiveTable: React.FC<ArchiveTableProps> = ({
  items,
  refreshExperiments,
}) => {
  const { hasAdoAccess } = useDataAccessContext();
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [isSortDescending, setIsSortDescending] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [
    isUnarchiveDialogHidden,
    { setTrue: hideUnarchiveDialog, setFalse: showUnarchiveDialog },
  ] = useBoolean(true);
  const [experimentToUnarchive, setExperimentToUnarchive] =
    useState<ExperimentType | null>(null);

  const { extensionName = "" } = useEv2ExperimentParams();
  const [appliedFilters, setAppliedFilters] = useLocalStorageState<{
    [key: string]: string[];
  }>(`archiveAppliedFilters-${extensionName}`, {});
  const [searchString, setSearchString] = useLocalStorageState<string>(
    `archiveSearchString-${extensionName}`,
    "",
  );

  const { trackOperationEvent } = useAppInsights("ArchiveTable");

  const trackSearchTelemetry = useMemo(
    () =>
      debounce((value: string) => {
        trackOperationEvent({
          function: "onSearchChange",
          operation: "Search",
          properties: { searchString: value },
        });
      }, 500),
    [trackOperationEvent],
  );

  const onSearchChange = (value: string) => {
    setSearchString(value);
    trackSearchTelemetry(value);
  };

  const onAppliedFiltersChange = useMemo(
    () => (newFilters: { [key: string]: string[] }) => {
      setAppliedFilters(newFilters);
      trackOperationEvent({
        function: "onAppliedFiltersChange",
        operation: "FilterChange",
        properties: { appliedFilters: newFilters },
      });
    },
    [setAppliedFilters, trackOperationEvent],
  );

  const handleColumnClick = createColumnClickHandler(
    setSortColumn,
    setIsSortDescending,
    sortColumn,
    isSortDescending,
  );

  const closeUnarchiveDialog = () => {
    hideUnarchiveDialog();
    trackOperationEvent({
      operation: "CloseUnarchiveDialog",
      properties: { experiment: experimentToUnarchive },
    });
  };

  const handleUnarchive = (experiment: ExperimentType) => {
    setExperimentToUnarchive(experiment);
    showUnarchiveDialog();
    trackOperationEvent({
      operation: "OpenUnarchiveDialog",
      properties: { experiment },
    });
  };

  const onUnarchiveSuccess = () => {
    refreshExperiments();
    closeUnarchiveDialog();
  };

  const columns = getArchiveTableColumns({
    handleUnarchive,
    sortState: { sortColumn: sortColumn, isSortDescending: isSortDescending },
    hasAdoAccess,
    onColumnClick: (ev: React.MouseEvent<HTMLElement>, column: IColumn) => {
      handleColumnClick(ev, column);
      trackOperationEvent({
        function: "onColumnClick",
        operation: "Sort",
        properties: { columnKey: column.key, isDescending: isSortDescending },
      });
    },
  });

  // Calculate the minimum width of the table based on the columns
  // This is used to set the width of the table so the table will correctly apply horizontal scrolling when there are many columns or when the screen is small
  const tableMinWidth = useMemo(
    () =>
      columns.reduce((totalWidth, column) => totalWidth + column.minWidth, 0),
    [columns],
  );

  const filters = useMemo(
    () => getFilters(items, appliedFilters, setAppliedFilters),
    [items, appliedFilters, setAppliedFilters],
  );

  // Apply filtering
  const filteredItems = useTableFiltering(items, {
    searchString,
    searchFields: ["featureName", "featureDescription"],
    appliedFilters,
  });

  // Apply sorting
  const sortedItems = useTableSorting(filteredItems, {
    sortColumn,
    isSortDescending,
  });

  return (
    <>
      <FilterComponent
        searchOptions={{
          placeholder: "Search archived experiment name and description",
          value: searchString,
          onChange: (_, newValue) => {
            onSearchChange(newValue || "");
            trackSearchTelemetry(newValue || "");
          },
        }}
        filters={filters}
        onClearFilters={() => {
          onAppliedFiltersChange({});
          trackOperationEvent({
            operation: "ClearFilters",
            properties: { filters: appliedFilters },
          });
        }}
      />
      <Stack
        className={mergeStyles(sharedStyles.detailsListWrapperWithStickyHeader)}
      >
        <ScrollablePane scrollbarVisibility={ScrollbarVisibility.auto}>
          <Stack style={{ width: `max(100%, ${tableMinWidth}px)` }}>
            <ShimmeredDetailsList
              className={mergeStyles(styles.experimentsTable)}
              items={sortedItems}
              columns={columns}
              selectionMode={SelectionMode.none}
              getKey={(item) => item?.__fullpath || item?.featureKey}
              layoutMode={DetailsListLayoutMode.justified}
              onRenderDetailsHeader={(props, defaultRender) => {
                return (
                  <Sticky stickyPosition={StickyPositionType.Header}>
                    {defaultRender && defaultRender(props)}
                  </Sticky>
                );
              }}
            />
            {sortedItems.length === 0 && (
              <Stack>
                <Text>No archived experiments...</Text>
              </Stack>
            )}
          </Stack>
        </ScrollablePane>
      </Stack>
      {
        <SharedDialog
          isOpen={!isUnarchiveDialogHidden}
          onDismiss={closeUnarchiveDialog}
          onConfirm={() =>
            onUnarchive(
              experimentToUnarchive!,
              setIsLoading,
              onUnarchiveSuccess,
              setError,
            )
          }
          title="Unarchive Experiment"
          subText={`Are you sure you want to unarchive the experiment "${experimentToUnarchive?.featureName}"?`}
          isSubmitting={isLoading}
          confirmButtonText="Yes, unarchive"
          disableButtonText="No, cancel"
          confirmButtonDisabled={isLoading}
          disableMaxWidth={true}
        >
          {error && (
            <>
              <MessageBar messageBarType={MessageBarType.error}>
                {error}
              </MessageBar>
              <br />
            </>
          )}
          This will move the experiment back to the active experiments list so
          they can be managed.
        </SharedDialog>
      }
      <FilterTotalsComponent
        totalItemsCount={items.length}
        filteredItemsCount={filteredItems.length}
        appliedFilters={appliedFilters}
        searchString={searchString}
      />
    </>
  );
};

/**
 * Handles the unarchiving of an experiment. Will create a new branch, rename the experiment file to remove the archive key,
 * and create a pull request for the unarchive operation.
 * @param experiment The experiment to unarchive.
 * @param setIsLoading Function to set the loading state while the experiment is being unarchived.
 * @param onSuccess Callback function to execute on successful unarchive.
 */
const onUnarchive = async (
  experiment: ExperimentType,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  onSuccess: () => void,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
) => {
  const branchName = `unarchive-${experiment.featureKey}`;
  setIsLoading(true);
  const extensionName = experiment.__extension;
  const tracker = createBaseOperationTracker("Unarchive", "onUnarchive", {
    component: "ArchiveTable",
    experimentName: experiment.featureKey,
    extensionName,
  });
  tracker.start({ branchName, experiment });

  try {
    await createAdoBranch(branchName, "main");
    tracker.step("BranchCreated", { branchName });

    const itemPath = experiment.__fullpath;
    const newItemPath = itemPath.replace(`${ARCHIVE_KEY}.json`, `.json`);
    // Fetch the current file content to preserve it during rename
    const fileContent = await fetchAdoContent(itemPath);
    // Convert the raw content back to JSON string for the rename operation
    const contentString =
      typeof fileContent.__raw === "string"
        ? fileContent.__raw
        : JSON.stringify(fileContent.__raw, null, 2);

    const changes: PullRequestChange[] = [
      {
        changeType: "rename",
        sourceServerItem: itemPath,
        itemPath: newItemPath,
        newContent: contentString,
        contentType: "rawtext",
      },
    ];

    const commitMessage = `[${UNARCHIVE_KEY}] Experiment ${experiment.featureKey}`;

    await pushToAdoBranch({ branchName, commitMessage, changes });
    tracker.step("FilesPushed", { branchName, commitMessage });

    await createAdoPullRequest({
      sourceBranch: branchName,
      targetBranch: "main",
      title: `[${UNARCHIVE_KEY}] Experiment: ${experiment.featureKey}`,
      description: `[${UNARCHIVE_KEY}] Experimentation UI Request to unarchive experiment: ${experiment.featureKey}. Once this PR is merged, the experiment will be unarchived and will be manageable via the UI again.`,
    });
    tracker.step("PullRequestCreated", { branchName });

    tracker.succeed();
    onSuccess();
  } catch (error) {
    tracker.fail(error instanceof Error ? error.message : String(error));
    setError("Failed to unarchive experiment: " + (error as string));
    console.error("Error unarchiving experiment:", error);
    baseTrackExceptionEvent(error as Error | string, SeverityLevel.Error, {
      component: "ArchiveTable",
      extensionName,
      experimentName: experiment.featureKey,
      function: "onUnarchive",
    });
  } finally {
    setIsLoading(false);
  }
};

export default ArchiveTable;
